'use client';
/**
 * LE CIRCUIT, VU DEPUIS LA FICHE (Ines, 17-18/10/2026).
 *
 * ── Ce que ce bandeau remplace ──────────────────────────────────────────────
 *
 * Pour faire avancer une modification, il fallait quitter l'examen : aller à
 * l'onglet « Circuit » du référentiel, retrouver la ligne, déplier, cliquer.
 * Douze à quatorze clics, et au milieu, une fiche qui ne disait pas elle-même
 * qu'un brouillon attendait. Le constat de la RQ : « on ne sait pas où en est
 * une fiche ».
 *
 * Désormais la fiche DIT son état en tête — brouillon, à relire, à diffuser,
 * refusée — et porte les actes qui s'y rapportent : proposer, approuver,
 * refuser, diffuser. L'onglet « Circuit » reste la vue d'ensemble de tout ce
 * qui attend dans le laboratoire ; il ne modifie plus rien lui-même.
 *
 * ── Ce qui NE change pas ────────────────────────────────────────────────────
 *
 * Les gardes sont au moteur, pas ici : rôle `rq` pour approuver et diffuser,
 * refus d'approuver sa propre proposition, aucune diffusion avant la date
 * d'application. Le bandeau MONTRE ces règles — le bouton absent avec sa raison
 * vaut mieux qu'un 403 après le clic — mais ne les tient pas.
 *
 * ⚠️ On n'approuve que ce qu'on a relu : l'approbation renvoie le diff que la
 * fiche affiche (`diffLu`). Si la proposition a bougé depuis, le moteur refuse
 * (409) et la fiche se recharge.
 */
import React, { useState } from 'react';
import { Pastille, useConfirmation } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { fmtDate } from '@/lib/meta';
import { moteur } from './Registres';

export type StatutCircuit = 'brouillon' | 'propose' | 'valide' | 'rejete';
export type DiffChamp = { champ: string; avant: unknown; apres: unknown };
/** Le brouillon d'UN examen, tel que `manuel-circuit/brouillon/:code` le rend. */
export type BrouillonFiche = {
  documentId: string | null;
  statut: StatutCircuit;
  author: string | null; date: string | null; dateApp: string | null; observation: string | null;
  champs: string[]; diff: DiffChamp[]; values: Record<string, unknown>;
  /** Les champs OPPOSABLES que ce brouillon change (règle du moteur,
   *  `circuit-opposable.ts`). Vide = correction simple : le RQ diffuse seul. */
  opposables: string[];
};

/** Ce que la liste et la fiche disent d'une proposition en cours — les mêmes
 *  quatre états que le moteur, avec les mots de la RQ. */
export const PASTILLE_CIRCUIT: Record<StatutCircuit, { libelle: string; ton: 'neutral' | 'warning' | 'info' | 'danger'; aide: string }> = {
  brouillon: { libelle: 'brouillon', ton: 'neutral', aide: 'Une modification est enregistrée mais pas encore proposée.' },
  propose: { libelle: 'à relire', ton: 'warning', aide: 'Proposée : un autre RQ doit l’approuver, depuis la fiche.' },
  valide: { libelle: 'à diffuser', ton: 'info', aide: 'Approuvée : il reste à diffuser, depuis la fiche.' },
  rejete: { libelle: 'refusée', ton: 'danger', aide: 'Refusée avec motif : à corriger ou à abandonner.' },
};

/** Lit la réponse de `manuel-circuit/brouillon/:code` ; `null` s'il n'y a rien. */
export function lireBrouillon(data: Record<string, unknown>): BrouillonFiche | null {
  if (!data?.existe) return null;
  return {
    documentId: (data.documentId as string) || null,
    statut: ((data.statut as StatutCircuit) || 'brouillon'),
    author: (data.author as string) || null, date: (data.date as string) || null,
    dateApp: (data.dateApp as string) || null, observation: (data.observation as string) || null,
    champs: Array.isArray(data.champs) ? (data.champs as string[]) : [],
    diff: Array.isArray(data.diff) ? (data.diff as DiffChamp[]) : [],
    values: (data.values as Record<string, unknown>) || {},
    opposables: Array.isArray(data.opposables) ? (data.opposables as string[]) : [],
  };
}

/** La liste des champs opposables, servie par le moteur avec le brouillon
 *  (même quand il n'y en a pas) — jamais recopiée côté écran. */
export function lireChampsOpposables(data: Record<string, unknown>): string[] {
  return Array.isArray(data?.champsOpposables) ? (data.champsOpposables as string[]) : [];
}

const LIBELLE_CHAMP: Record<string, string> = {
  materiels: 'contenants acceptés', milieux: 'milieux prélevés', echantillons: 'matières',
};

export function BandeauCircuit({ code, brouillon, estRq, moi, libelles, onFait }: {
  code: string;
  brouillon: BrouillonFiche;
  estRq: boolean;
  /** Le nom de la personne connectée — pour montrer la séparation des tâches. */
  moi: string;
  /** Libellé lisible d'un champ du catalogue, par clé. */
  libelles: (champ: string) => string;
  /** Après tout acte : la fiche recharge son brouillon et sa liste. */
  onFait: () => void;
}) {
  const { demander, demanderTexte, confirmation } = useConfirmation();
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const p = PASTILLE_CIRCUIT[brouillon.statut];
  const estAuteur = !!brouillon.author && brouillon.author === moi;

  const acte = async (action: string, corps?: unknown) => {
    if (!brouillon.documentId) { setErreur('Le moteur n’a pas rendu l’identifiant du brouillon : rechargez la page.'); return; }
    setEnvoi(true); setErreur('');
    const r = await moteur(`manuel-circuit/${brouillon.documentId}/${action}`, corps ?? {});
    setEnvoi(false);
    if (!r.ok) { setErreur(r.message); onFait(); return; }
    onFait();
  };
  const approuver = async () => {
    const dateApp = brouillon.dateApp || await demanderTexte('Date d’application — elle borne la diffusion :',
      { titre: `Approuver la proposition sur ${code}`, confirmer: 'Approuver', type: 'date' });
    if (!dateApp) return;
    await acte('valider', { dateApp, diffLu: brouillon.diff });
  };
  const refuser = async () => {
    const observation = await demanderTexte('Motif du refus (obligatoire — il est journalisé) :',
      { titre: `Refuser la proposition sur ${code}`, confirmer: 'Refuser' });
    if (!observation) return;
    await acte('refuser', { observation });
  };
  /* LA VITESSE RAPIDE (18/10/2026) : rien d'opposable dans le diff → le RQ
     diffuse seul. Le moteur revérifie et refuse si un champ opposable s'est
     glissé entre-temps. */
  const simple = brouillon.opposables.length === 0 && brouillon.diff.length > 0;
  const corriger = async () => {
    const ok = await demander(
      `Diffuser cette correction sur ${code} sans seconde relecture : ${brouillon.champs.map((c) => LIBELLE_CHAMP[c] || libelles(c)).join(', ')}. La fiche publique change tout de suite ; le journal l’inscrit à votre nom.`,
      { titre: 'Diffuser la correction', confirmer: 'Oui, diffuser' },
    );
    if (!ok) return;
    await acte('corriger');
  };
  const diffuser = async () => {
    const ok = await demander(
      `La fiche publique de ${code} va changer tout de suite : l’ancienne valeur ne fera plus foi, et la version de l’examen monte au chiffre entier suivant.`,
      { titre: 'Diffuser', confirmer: 'Oui, diffuser' },
    );
    if (!ok) return;
    setEnvoi(true); setErreur('');
    const r = await moteur('manuel-circuit/diffuser', { codes: [code] });
    setEnvoi(false);
    if (!r.ok) { setErreur(r.message); return; }
    const d = r.data as { echecs?: string[] };
    if (d.echecs?.length) setErreur(d.echecs.join(' ; '));
    onFait();
  };

  const champs = brouillon.champs.map((c) => LIBELLE_CHAMP[c] || libelles(c));
  return (
    <div role="status" style={{
      display: 'grid', gap: 8, padding: '10px 14px', marginTop: 12,
      borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)',
      background: brouillon.statut === 'rejete' ? 'var(--red-50)' : brouillon.statut === 'propose' ? 'var(--amber-50)' : 'var(--cyan-50)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12.5, lineHeight: 1.5 }}>
        <Pastille ton={p.ton} title={p.aide}>{p.libelle}</Pastille>
        <span style={{ flex: '1 1 260px', color: 'var(--text-body)' }}>
          {brouillon.statut === 'brouillon' && <>Une modification est enregistrée sur cet examen, pas encore proposée.</>}
          {brouillon.statut === 'propose' && <>Une proposition attend sa relecture par un autre RQ.</>}
          {brouillon.statut === 'valide' && <>Approuvée{brouillon.dateApp ? <>, applicable le <strong>{fmtDate(brouillon.dateApp)}</strong></> : null} : il reste à diffuser.</>}
          {brouillon.statut === 'rejete' && <>Refusée{brouillon.observation ? <> — <em>{brouillon.observation}</em></> : null}.</>}
          {brouillon.author ? <> Déposée par <strong>{brouillon.author}</strong></> : null}
          {brouillon.date ? <> le {fmtDate(brouillon.date)}</> : null}.
          {champs.length ? <> Champs touchés : <strong>{champs.join(' · ')}</strong>.</> : null}
          {/* Deux vitesses : la fiche DIT quel chemin la modification prend, et
              pourquoi — avant qu'on clique. */}
          {brouillon.statut !== 'valide' && brouillon.statut !== 'rejete' && (
            brouillon.opposables.length
              ? <> <strong>Double relecture</strong> : {brouillon.opposables.length > 1 ? 'des champs opposables aux préleveurs et confrères sont touchés' : 'un champ opposable aux préleveurs et confrères est touché'} ({brouillon.opposables.map((c) => LIBELLE_CHAMP[c] || libelles(c)).join(', ')}) — un autre RQ approuve, avec une date d’application.</>
              : simple ? <> <strong>Correction simple</strong> : aucun champ opposable — le RQ peut la diffuser seul, le journal l’inscrit.</> : null
          )}
          {' '}Les valeurs en attente sont <strong>surlignées</strong> dans la fiche, l’ancienne barrée à côté ; la fiche publique ne change qu’à la diffusion.
        </span>
        <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
          {brouillon.statut === 'brouillon' && simple && estRq && (
            <Bouton variante="primaire" taille="sm" disabled={envoi} onClick={() => void corriger()}>Diffuser la correction</Bouton>
          )}
          {brouillon.statut === 'brouillon' && (
            <Bouton variante={simple && estRq ? 'contour' : 'primaire'} taille="sm" disabled={envoi}
              title={simple && estRq ? 'Demander quand même une seconde relecture' : undefined}
              onClick={() => void acte('proposer')}>Proposer à la relecture</Bouton>
          )}
          {brouillon.statut === 'propose' && simple && estRq && estAuteur && (
            <Bouton variante="primaire" taille="sm" disabled={envoi} onClick={() => void corriger()}>Diffuser la correction</Bouton>
          )}
          {brouillon.statut === 'propose' && estRq && !estAuteur && (
            <>
              <Bouton variante="primaire" taille="sm" disabled={envoi} onClick={() => void approuver()}>Approuver</Bouton>
              <Bouton variante="danger" taille="sm" disabled={envoi} onClick={() => void refuser()}>Refuser</Bouton>
            </>
          )}
          {brouillon.statut === 'propose' && estAuteur && (
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)', alignSelf: 'center' }}>votre proposition — un autre RQ relit</span>
          )}
          {brouillon.statut === 'valide' && estRq && (
            <>
              <Bouton variante="primaire" taille="sm" disabled={envoi} onClick={() => void diffuser()}>Diffuser</Bouton>
              <Bouton taille="sm" disabled={envoi} onClick={() => void acte('devalider')}>Dévalider</Bouton>
            </>
          )}
        </span>
      </div>
      {erreur && <div role="alert" style={{ color: 'var(--red-600)', fontSize: 12.5 }}>{erreur}</div>}
      {confirmation}
    </div>
  );
}

/** Une valeur du dossier quand un brouillon la remplace : l'ancienne barrée,
 *  la nouvelle surlignée. C'est l'« Avant / après », ligne à ligne, là où on
 *  lit la valeur — plus un onglet à part. */
export function ValeurAvecBrouillon({ publie, brouillon }: { publie: React.ReactNode; brouillon: React.ReactNode }) {
  const vide = (v: React.ReactNode) => v === null || v === undefined || v === '';
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '2px 8px', alignItems: 'baseline' }}>
      <span style={{ color: 'var(--text-faint)', textDecoration: vide(publie) ? undefined : 'line-through', fontStyle: vide(publie) ? 'italic' : undefined }}>
        {vide(publie) ? 'vide' : publie}
      </span>
      <span style={{ background: 'var(--amber-50)', color: 'var(--amber-700)', fontWeight: 600, padding: '0 5px', borderRadius: 'var(--radius-xs)', whiteSpace: 'pre-wrap' }}>
        {vide(brouillon) ? <em>effacé</em> : brouillon}
      </span>
      <Pastille compacte ton="warning" title="En attente dans le circuit — la fiche publique ne change qu’à la diffusion.">brouillon</Pastille>
    </span>
  );
}
